import {useContext} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {Body2, Headline1, Headline2} from '@adv-mt/ui/typography'

import {scrollToSection} from '../../../utils.js'
import {BASE_CLASS} from './config.js'

const Financing = ({ad = {}, financingData, isPersonalLoanAvailable}) => {
  const {i18n, domain} = useContext(Context)
  const {track: analyticsTrack} = useAnalyticsTrack()

  const config = domain.get('config')
  const {TRACKING_EVENT_CONTEXTS, TRACKING_EVENT_NAMES} = config.get('tracking')
  const {AD_DETAIL_PAGE_SECTION_IDS} = config.get('COMMON').AD_DETAIL_PAGE_SECTIONS

  const {isProfessional} = ad

  const isHigherThanMobile = useMediaQuery(useMediaQueryBreakpoints.sm, {
    defaultMatches: !config.get('isMobile')
  })

  const Instalment = isHigherThanMobile ? Headline1 : Headline2

  const monthlyInstalment = financingData?.fee
  const totalFinancedPrice = financingData?.computedPrice || financingData?.financedPrice || ad.price
  const cashPaymentEntry = financingData?.entry || financingData?.minEntry || ad.financingInfo?.entry

  const showDealerFinancing = Boolean(monthlyInstalment && isProfessional)
  const showPersonalLoan = Boolean(monthlyInstalment && isPersonalLoanAvailable)

  const trackMoreInfoClicked = async () => {
    const eventData = await domain.get('get_event_tracking_data_use_case').execute({
      ad,
      context: TRACKING_EVENT_CONTEXTS.FINANCING_PRICE_INFO,
      event: showDealerFinancing
        ? TRACKING_EVENT_NAMES.FINANCING_CALCULATOR_CLICKED
        : TRACKING_EVENT_NAMES.FINANCING_PRI_CALCULATOR_CLICKED
    })

    analyticsTrack(eventData)
  }

  // Both links land on the same place. The financing panel holds the dealer offer and the personal
  // loan calculator alike — it picks which one to render from the ad, so there is nothing finer to
  // aim at. Tracking still tells the two apart through the event name.
  const onClickMoreInfo = () => {
    scrollToSection(AD_DETAIL_PAGE_SECTION_IDS.FINANCING)
    trackMoreInfoClicked()
  }

  const onKeyDownMoreInfo = event => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    onClickMoreInfo()
  }

  const {SHOW_CUSTOM_MONTHLY_FEE} = config.get('search')
  const showCustomMonthlyFee = SHOW_CUSTOM_MONTHLY_FEE && Boolean(ad.monthlyFee) && isProfessional

  if (!showDealerFinancing && !showPersonalLoan) {
    if (!showCustomMonthlyFee) return null

    return (
      <div className={`${BASE_CLASS}-financed`}>
        <div className={`${BASE_CLASS}-financedContent`}>
          <Body2 className={`${BASE_CLASS}-financedContentLabel`}>{i18n.t('COMMON.FEE_FROM')}</Body2>
          <Instalment as="p" className={`${BASE_CLASS}-instalment`}>
            {i18n.c(ad.monthlyFee)}
            <Body2 as="span" highlight>{`/${i18n.t('COMMON.PLURALS.MONTH', 1).toLowerCase()}`}</Body2>
          </Instalment>
        </div>
      </div>
    )
  }

  return (
    <div className={`${BASE_CLASS}-financed`}>
      <div className={`${BASE_CLASS}-financedContent`}>
        <Body2 className={`${BASE_CLASS}-financedContentLabel`}>
          {showDealerFinancing
            ? `${i18n.t('COMMON.FINANCED')}: ${i18n.c(totalFinancedPrice)}`
            : i18n.t('COMMON.FEE_MONTHLY_PERSONAL_LOAN')}
        </Body2>
        <Instalment as="p" className={`${BASE_CLASS}-instalment`}>
          {i18n.c(monthlyInstalment)}
          <Body2 as="span" highlight>{`/${i18n.t('COMMON.PLURALS.MONTH', 1).toLowerCase()}*`}</Body2>
        </Instalment>
      </div>
      {showPersonalLoan && Boolean(cashPaymentEntry) && (
        <Body2 className={`${BASE_CLASS}-financedEntry`}>
          {i18n.t('WITH_A_CASH_PAYMENT_OF', {amount: i18n.c(cashPaymentEntry)})}
        </Body2>
      )}
      <Body2
        link
        highlight
        className={`${BASE_CLASS}-financedMoreInfo`}
        onClick={onClickMoreInfo}
        onKeyDown={onKeyDownMoreInfo}
        tabIndex={0}
      >
        {i18n.t(showDealerFinancing ? 'COMMON.KNOW_MORE' : 'COMMON.CALCULATE_FEE')}
      </Body2>
    </div>
  )
}

Financing.displayName = 'Financing'
Financing.propTypes = {
  ad: PropTypes.shape({
    financingInfo: PropTypes.shape({entry: PropTypes.number}),
    isProfessional: PropTypes.bool,
    monthlyFee: PropTypes.number,
    price: PropTypes.number
  }),
  financingData: PropTypes.shape({
    computedPrice: PropTypes.number,
    entry: PropTypes.number,
    fee: PropTypes.number,
    financedPrice: PropTypes.number,
    minEntry: PropTypes.number
  }),
  isPersonalLoanAvailable: PropTypes.bool
}

export default Financing
